import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Route as RouteIcon, Download, Calendar, FileSpreadsheet } from "lucide-react";

export default function HistorialReportes() {
  const [grupos, setGrupos] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/route-reports", { params });
      setGrupos(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [desde, hasta]);

  const descargar = async (rid) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/route-reports/${rid}.xlsx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `plan_ruta_${rid}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success("Descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalReports = grupos.reduce((n, g) => n + g.count, 0);
  const totalPedidos = grupos.reduce((n, g) => n + g.total_pedidos, 0);

  return (
    <div className="space-y-6" data-testid="historial-reportes-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Distribución</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <RouteIcon className="w-8 h-8 text-[#F59E0B]" /> Historial de Reportes de Ruta
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Cada vez que exportas un Reporte de Ruta desde Hoja de Pedidos se guarda aquí, agrupado por día.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs uppercase tracking-widest">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="hist-desde" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="hist-hasta" />
          </div>
          {(desde || hasta) && (
            <Button variant="outline" onClick={() => { setDesde(""); setHasta(""); }} data-testid="hist-clear">Limpiar</Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Días con reportes</div>
          <div className="font-manrope font-black text-3xl mt-1">{grupos.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Reportes totales</div>
          <div className="font-manrope font-black text-3xl mt-1">{totalReports}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos incluidos</div>
          <div className="font-manrope font-black text-3xl mt-1">{totalPedidos}</div>
        </Card>
      </div>

      {loading && <div className="text-sm text-zinc-500">Cargando...</div>}
      {!loading && grupos.length === 0 && (
        <Card className="p-8 border-zinc-200 rounded-md text-center text-zinc-400">
          <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
          Aún no hay reportes exportados. Ve a Hoja de Pedidos, selecciona pedidos y usa "Reporte de Ruta".
        </Card>
      )}

      {grupos.map((g) => (
        <Card key={g.fecha} className="border-zinc-200 rounded-md overflow-hidden" data-testid={`hist-dia-${g.fecha}`}>
          <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-zinc-500" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Fecha</div>
                <div className="font-manrope font-black text-lg">{g.fecha}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-600">
              <b>{g.count}</b> reportes · <b>{g.total_pedidos}</b> pedidos
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Código</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Hora</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Generado por</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Pedidos</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {g.reports.map((r) => (
                  <tr key={r.id} className="hover:bg-zinc-50" data-testid={`hist-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{r.codigo_reporte}</td>
                    <td className="px-4 py-3 text-xs">{(r.fecha || "").slice(11, 16)}</td>
                    <td className="px-4 py-3 text-xs">{r.generado_por}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-zinc-700">{(r.order_ids || []).length} pedidos</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {(r.order_summaries || []).slice(0, 4).map((s) => s.numero).join(", ")}
                        {(r.order_summaries || []).length > 4 && ` +${r.order_summaries.length - 4}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => descargar(r.id)}
                        className="gap-1"
                        data-testid={`hist-download-${r.id}`}
                      >
                        <Download className="w-3 h-3" /> XLSX
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}
